import { useCallback, type RefObject } from "react";
import { Box, SimpleGrid, type SimpleGridProps } from "@mantine/core";
import { type CompanyWithUI } from "@/components/shared/CompanyCardHorizontal";
import { useAppShellSidebarExpanded } from "@/components/layout/AppShellSidebarContext";
import { useTabletViewport } from "@/shared/hooks/useTabletViewport";
import DiscoverCompanyCard from "@/pages/discover/components/DiscoverCompanyCard";

export type DiscoverCompaniesTableProps = {
  companies: CompanyWithUI[];
  /** Query string appended to `/discover/:id` links (e.g. `?keyword=...`). */
  profileSearch?: string;
  /** Passed through link state so back navigation restores discover filters. */
  discoverReturnTo?: string;
  /**
   * Override the "logged-in profile" path. Used by non-client portals (e.g. facilitator)
   * to open their own company detail route rather than `/my-discovery/:id`.
   */
  loggedInProfilePathBuilder?: (companyId: string) => string;
  /** Scroll target for pagination/page-size changes (should sit just above the results list). */
  headerAnchorRef?: RefObject<HTMLDivElement | null>;
  canViewCompanyDetails?: boolean;
  onBlockedViewCompanyDetails?: () => void;
  onViewDetails: (companyId: string) => void;
  onShortlistClick: (companyId: string, isShortlisted: boolean) => void | Promise<void>;
  onGuestShortlistClick?: (companyId: string) => void;
  onGuestShortlistRemove?: (companyId: string) => void;
  /** When set to `collaborationIntent`, the secondary meta column shows collaboration models instead. */
  secondaryColumn?: "industry" | "collaborationIntent";
  /** Guest discover: 5 cards per row on large screens (10 cards = 2 rows at default page size). */
  gridCols?: SimpleGridProps["cols"];
  compact?: boolean;
};

export default function DiscoverCompaniesTable({
  companies,
  profileSearch = "",
  discoverReturnTo,
  loggedInProfilePathBuilder,
  headerAnchorRef,
  canViewCompanyDetails = true,
  onBlockedViewCompanyDetails,
  onViewDetails,
  onShortlistClick,
  onGuestShortlistClick,
  onGuestShortlistRemove,
  secondaryColumn = "industry",
  gridCols,
  compact = false,
}: DiscoverCompaniesTableProps) {
  const isTabletTable = useTabletViewport();
  const sidebarExpanded = useAppShellSidebarExpanded();
  const hideActionsOnTablet = isTabletTable && sidebarExpanded;

  const handleViewCompany = useCallback(
    (companyId: string) => {
      if (!canViewCompanyDetails) {
        onBlockedViewCompanyDetails?.();
        return;
      }
      onViewDetails(companyId);
    },
    [canViewCompanyDetails, onBlockedViewCompanyDetails, onViewDetails],
  );

  return (
    <Box w="100%" maw="100%" style={{ minWidth: 0 }}>
      <div ref={headerAnchorRef} style={{ scrollMarginTop: 96 }} aria-hidden />
      <SimpleGrid
        type={gridCols ? "container" : undefined}
        cols={gridCols ?? { base: 1, md: 2 }}
        spacing={compact ? "sm" : "md"}
      >
        {companies.map((company) => (
          <DiscoverCompanyCard
            key={company.id}
            company={company}
            compact={compact}
            profileSearch={profileSearch}
            discoverReturnTo={discoverReturnTo}
            loggedInProfilePathBuilder={loggedInProfilePathBuilder}
            canViewCompanyDetails={canViewCompanyDetails}
            showShortlistButton={!hideActionsOnTablet}
            secondaryColumn={secondaryColumn}
            onViewDetails={handleViewCompany}
            onShortlistClick={onShortlistClick}
            onGuestShortlistClick={onGuestShortlistClick}
            onGuestShortlistRemove={onGuestShortlistRemove}
          />
        ))}
      </SimpleGrid>
    </Box>
  );
}
